import { useState, useEffect } from 'react'

/**
 * Chicken explosion animation with Easter egg confetti
 * Triggered when puzzle is completed
 */
export default function ChickenExplosion({ onAnimationComplete }) {
  const [particles, setParticles] = useState([])
  const [showChicken, setShowChicken] = useState(true)

  useEffect(() => {
    // Start explosion after a short delay
    const explosionTimer = setTimeout(() => {
      setShowChicken(false)
      createExplosion()

      // Animation completes after confetti animation ends
      const completeTimer = setTimeout(() => {
        if (onAnimationComplete) {
          onAnimationComplete()
        }
      }, 3000)

      return () => clearTimeout(completeTimer)
    }, 600)

    return () => clearTimeout(explosionTimer)
  }, [onAnimationComplete])

  function createExplosion() {
    const newParticles = []
    const particleCount = 40

    for (let i = 0; i < particleCount; i++) {
      const angle = (360 / particleCount) * i + (Math.random() - 0.5) * 30
      const velocity = 4 + Math.random() * 4
      const radians = (angle * Math.PI) / 180

      newParticles.push({
        id: i,
        emoji: ['🥚', '🐥', '🌸'][Math.floor(Math.random() * 3)],
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
        vx: Math.cos(radians) * velocity,
        vy: Math.sin(radians) * velocity,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 15,
        life: 1,
        scale: 1 + Math.random() * 0.5,
        gravity: 0.15
      })
    }

    setParticles(newParticles)

    // Animate particles
    let frame = 0
    const maxFrames = 180
    const animateParticles = () => {
      frame++

      const updated = newParticles.map(p => ({
        ...p,
        x: p.x + p.vx,
        y: p.y + p.vy,
        vy: p.vy + p.gravity,
        rotation: p.rotation + p.rotationSpeed,
        life: 1 - frame / maxFrames
      }))

      setParticles(updated)

      if (frame < maxFrames) {
        requestAnimationFrame(animateParticles)
      }
    }

    requestAnimationFrame(animateParticles)
  }

  return (
    <div style={styles.container}>
      {/* Jumping and laughing chicken */}
      {showChicken && (
        <div style={styles.chicken}>
          <span style={{ fontSize: '80px', animation: 'bounce 0.5s ease-in-out' }}>
            🐔
          </span>
          <div style={styles.laughText}>
            Bock bock bock! 😄
          </div>
        </div>
      )}

      {/* Confetti particles */}
      {particles.map(particle => (
        <div
          key={particle.id}
          style={{
            position: 'fixed',
            left: particle.x,
            top: particle.y,
            fontSize: '32px',
            pointerEvents: 'none',
            opacity: particle.life,
            transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
            transition: 'none'
          }}
        >
          {particle.emoji}
        </div>
      ))}

      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0) scaleY(1);
          }
          25% {
            transform: translateY(-30px) scaleY(0.8);
          }
          50% {
            transform: translateY(0) scaleY(1);
          }
          75% {
            transform: translateY(-20px) scaleY(0.85);
          }
        }
      `}</style>
    </div>
  )
}

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 999
  },
  chicken: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    pointerEvents: 'none'
  },
  laughText: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginTop: '10px',
    animation: 'fadeInOut 0.8s ease-in-out'
  }
}
